import { collection, deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { db, storage } from '@/config/firebase'
import type { ProofMetadata } from '@/types/file'
import type { Role } from '@/types/user'
import type { FileRecord } from './useProjectFiles'

export interface Uploader {
  uid: string
  name: string
  role: Role
}

/**
 * Upload a file to a project stage: Storage object first, then the metadata doc.
 * `metadata` carries EXIF geolocation/timestamp for Proof-of-Product photos.
 */
export async function uploadStageFile(
  projectId: string,
  stage: number,
  file: File,
  uploader: Uploader,
  metadata?: ProofMetadata,
): Promise<void> {
  const fileRef = doc(collection(db, 'projects', projectId, 'files'))
  const safeName = file.name.replace(/[^\w.-]+/g, '_')
  const storagePath = `projects/${projectId}/stages/${stage}/${fileRef.id}-${safeName}`

  await uploadBytes(ref(storage, storagePath), file, { contentType: file.type })
  await setDoc(fileRef, {
    stage,
    fileName: file.name,
    storagePath,
    contentType: file.type,
    size: file.size,
    uploadedByUid: uploader.uid,
    uploadedByName: uploader.name,
    uploadedByRole: uploader.role,
    uploadedAt: serverTimestamp(),
    ...(metadata
      ? {
          gps: metadata.gps,
          takenAt: metadata.takenAt,
          camera: metadata.camera,
          locationSource: metadata.locationSource ?? null,
        }
      : {}),
  })
}

export async function deleteStageFile(projectId: string, file: FileRecord): Promise<void> {
  await deleteObject(ref(storage, file.storagePath)).catch(() => {
    // Object may already be gone; the metadata delete below is what matters.
  })
  await deleteDoc(doc(db, 'projects', projectId, 'files', file.id))
}

export function fileDownloadURL(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath))
}
